import { createAction } from 'redux-actions';
import { isoParse, timeParse } from 'd3-time-format';

import {
  descriptionCreator,
  idCreator,
  metaCreator,
  payloadCreator,
} from './creator';
import fetch, {
  Api,
  getExtendedRequestConfig,
  getJson,
  handleError,
} from './fetch';
import { checkForCachedDataAction, requestAction } from './request';

export const dropboxFetchAction = createAction('GET_DROPBOX_DATA', payloadCreator, metaCreator);

// Dropbox reports all times as UTC, not all data should be treated as such.
// For instance, photos have their client_modified time adjusted from local time,
// but media_info.metadata.time_taken is not adjusted.
// We can use isoParse to parse the adjusted times, but should use this other parser
// (Z specified as a char rather than %Z indicating +0000) to parse 'local' times.
// const dropboxTimeParse = timeParse('%Y-%m-%dT%H:%M:%SZ');

const getThumbnailUrl = item => (
  `${Api}/dropbox-content/files/get_thumbnail?arg=${
    encodeURIComponent(JSON.stringify({
      path: item.path_lower,
      size: 'w256h256',
      mode: 'fitone_bestfit',
    }))}`
);

const getDownloadUrl = item => (
  `${Api}/dropbox-content/files/download?arg=${
    encodeURIComponent(JSON.stringify({ path: item.path_lower }))}`
);

const ROOT = `${Api}/dropbox/files/list_folder`;

const postConfig = {
  method: 'POST',
  headers: {
    'Content-Type': 'text/plain; charset=dropbox-cors-hack',
  },
  body: {
    path: '',
  },
};

function getPostConfig(path) {
  const config = {
    ...postConfig,
    body: JSON.stringify({
      ...postConfig.body,
      path,
    })
  };
  return (config);
}

const imageNameDateFormats = [
  { // 2021-05-19 19.44.51.jpg
    pattern: /[\d]{4}-[\d]{2}-[\d]{2} [\d]{2}.[\d]{2}.[\d]{2}/,
    format: '%Y-%m-%d %H.%M.%S',
  },
  { // IMG_20191209_121830.jpg, 20191209_121830_HRD.jpg, ...
    pattern: /[\d]{8}_[\d]{6}/,
    format: '%Y%m%d_%H%M%S',
  },
];

function parseImgDate(imageName) {
  for (let i = 0; i < imageNameDateFormats.length; i += 1) {
    const match = imageName.match(imageNameDateFormats[i].pattern);
    if (match) {
      return timeParse(imageNameDateFormats[i].format)(match[0]);
    }
  }
}

const parseTime = (item) => {
  let time;
  try {
    time = parseImgDate(item.name);
  } catch {}
  if (!time || Number.isNaN(time.getFullYear())) {
    try {
    time = new Date(item.name);
  } catch {}
  }
  if (!time || Number.isNaN(time.getFullYear())) {
    // Parsing from the name failed
    time = isoParse(item.client_modified);
  }
  if (!time) {
    console.error('Failed to parse date for Dropbox file', item.name);
    return undefined;
  }
  return time.toISOString();
};

const createBaseItem = item => ({
  id: item.id,
  time: parseTime(item),
  raw: item,
  name: item.name,
  href: getDownloadUrl(item),
});

const isImageFile = (item) => {
  const { name } = item;
  if (name) {
    const lowerCase = name.toLowerCase();
    if (lowerCase.indexOf('jpg') || lowerCase.indexOf('jpeg') || lowerCase.indexOf('png')) {
      return true;
    }
  }
  return false;
};

const fetchMore = (json) => {
  if (json.has_more) {
    const postConfig2 = { ...postConfig, body: JSON.stringify({ cursor: json.cursor }) };
    const moreConfig = getExtendedRequestConfig({ params: postConfig2 });
    return fetch(`${ROOT}/continue`, moreConfig)
      .then(handleError)
      .then(getJson)
      .then(fetchMore) // recurse!
      .then(entries => [...json.entries, ...entries]);
  }
  return json.entries;
};

function dirForParams(name, params) {
  const dirDate = new Date(name);
  const start = new Date(params.startTime);
  const end = new Date(params.endTime);
  if ((dirDate.getTime() >= start.getTime()) && (dirDate.getTime() < end.getTime())) {
    return true;
  }
  return false;
}

const fetchData = ({ path = '', params }) => 
  fetch(ROOT, getExtendedRequestConfig({ params: getPostConfig(path) }))
  .then(handleError)
  .then(getJson)
  .then(fetchMore)
  .then((entries) => {
    const notes = [];
    const photos = [];
    const promises = [];
    entries.forEach((d) => {
      if (d['.tag'] === 'folder') {
        if (dirForParams(d.name, params)) {
          promises.push(
            fetchData({ path: `${path}/${d.name}` })
            .then((folder) => {
              if (folder.notes.length) {
                notes.push(...folder.notes);
              }
              if (folder.photos.length) {
                photos.push(...folder.photos);
              }
          }));
        }
      } else {
        promises.push(
          Promise.resolve(createBaseItem(d))
          .then((item) => {
            if (!item.time) {
              // Unable to parse the time for the item, no reason to store it
              return;
            }
            if (isImageFile(d)) {
              item.thumbnailUrl = getThumbnailUrl(d);
              photos.push(item);
            } else {
              notes.push(item);
            }
          })
        );
      }
    });  
    return Promise.all(promises)
      .then(() => ({
        notes,
        photos,
      }));
  });

export const getDropboxData = ({ params }) => ((dispatch) => {
  const id = idCreator();
  const description = descriptionCreator({ source: 'Dropbox', params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetchData({ params }))
    .then(data => dispatch(dropboxFetchAction({ id, description, data })))
    .catch(error => dispatch(dropboxFetchAction({ id, description, error: error.message || error })));
});

export default getDropboxData;
